import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 90000,
});

// --- Request interceptor : auth header (si token présent) ----------------
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("aito_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// --- Response interceptor : gestion globale 401 --------------------------
// V11.8 — Stabilisation session preview :
// - Une simple erreur réseau (offline, timeout, DNS, etc.) NE DOIT PLUS
//   purger le token. Trop de "sessions expirées" fantômes se produisaient
//   quand le preview avait un lag transient.
// - Seules les 401 EXPLICITES (backend a répondu 401 avec un body/statut)
//   déclenchent la purge.
// - Élargissement des routes silencieuses : boot-time + endpoints
//   optionnels (compteurs, notifications, entity-links, campaigns pub).
const SILENT_401_PATTERNS = [
  /\/auth\/me$/,
  /\/athletes\/mine$/,
  /\/notifications(\/|$|\?)/,
  /\/entity-links\/inbox\/count/,
  /\/admin\/stats\/pending/,
  /\/copf\/stats\/pending/,
];
// Routes qui peuvent être appelées par un visiteur non connecté
// → un 401 dessus ne doit jamais provoquer de redirection
const PUBLIC_PATTERNS = [
  /\/auth\/(login|register|me)$/,
  /\/auth\/google\/session$/,
];

let isRedirectingFor401 = false;

api.interceptors.response.use(
  (r) => r,
  (error) => {
    // Erreur réseau (pas de réponse serveur) → ne JAMAIS toucher au token
    // (offline, timeout, DNS, preview qui reboot, etc.).
    if (!error?.response) {
      return Promise.reject(error);
    }

    const status = error.response.status;
    const url = error?.config?.url || "";

    if (status === 401) {
      const isSilent = SILENT_401_PATTERNS.some((re) => re.test(url));
      const isPublic = PUBLIC_PATTERNS.some((re) => re.test(url));
      const hadToken = !!localStorage.getItem("aito_token");

      if (!isSilent && !isPublic && hadToken) {
        // Token présent mais rejeté par le backend → session vraiment invalide.
        // 1) Purge le token
        try { localStorage.removeItem("aito_token"); } catch { /* ignore */ }
        // 2) Toast + redirection (une seule fois pour éviter le spam)
        if (!isRedirectingFor401) {
          isRedirectingFor401 = true;
          try { toast.error("Votre session a expiré. Merci de vous reconnecter."); } catch { /* ignore */ }
          // Petit délai pour laisser le toast s'afficher
          setTimeout(() => {
            const next = encodeURIComponent(window.location.pathname + window.location.search);
            const target = `/connexion?next=${next}`;
            // Évite la boucle si on est déjà sur /connexion
            if (!window.location.pathname.startsWith("/connexion")) {
              window.location.assign(target);
            }
            isRedirectingFor401 = false;
          }, 300);
        }
      }
      // Si pas de token + route publique/silent : on rejette sans bruit
    }

    return Promise.reject(error);
  },
);

export default api;
